import { Component, Inject, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CustomAttributesService } from '../../../services/custom-attributes.service';
import { NotificationService } from '../../../services/notifications.service';
import { Subject, takeUntil } from 'rxjs';
import { ERROR_MESSAGES, activeTranslations, LIST_CA } from '../../../enum/constants';
import { trimValidator } from '../../../helpers';
import { IError } from '../../../interfaces';
import { CreateForm } from './create-deals-fields.interface';
import * as _ from 'lodash';

@Component({
  templateUrl: './create-deals-fields.component.html',
  styleUrls: ['./create-deals-fields.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CreateDealsFields implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<null>();

  public list: any[] = [...LIST_CA];

  public createForm!: FormGroup<CreateForm>;
  public funnel_id!: string;
  public position: number = 1000;
  public field: any;

  public hasChanged: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<CreateDealsFields>,
    private readonly customAttributesService: CustomAttributesService,
    private readonly notificationService: NotificationService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.funnel_id = this.data.data.funnel_id;
    this.position = this.data.data.position;
    this.field = this.data.data.field;
  }

  ngOnInit(): void {
    if (this.field) {
      this.createForm = new FormGroup({
        title: new FormControl(this.field.title, [Validators.required, trimValidator]),
        type: new FormControl(this.list.find((v) => v.value === this.field.type)),
        key: new FormControl(this.field.key, [Validators.required, trimValidator]),
        is_view_card: new FormControl(this.field.is_view_card),
      });
    } else {
      this.createForm = new FormGroup({
        title: new FormControl('', [Validators.required, trimValidator]),
        type: new FormControl(this.list[0]),
        key: new FormControl('', [Validators.required, trimValidator]),
        is_view_card: new FormControl(false),
      });
    }
    this.onCreateFormValueChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  createField() {
    const { title, type, key, is_view_card } = this.createForm.getRawValue();
    this.createForm.markAllAsTouched();

    if (this.createForm.valid) {
      const payload = {
        funnel_id: this.funnel_id,
        title: title,
        type: type?.value,
        key: key,
        is_view_card: is_view_card,
        position: this.position + 1000,
      };
      this.customAttributesService
        .createCA(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.notificationService.showSuccess(
              '',
              activeTranslations.notifications_texts.success.successfully_saved,
            );
            this.dialogRef.close({
              success: true,
            });
          },
          error: (res) => {
            (res.error?.errors || []).forEach((err: IError) => {
              if (ERROR_MESSAGES[err.code] === ERROR_MESSAGES['CUSTOM_FIELD_ALREADY_EXISTS'])
                this.createForm.controls['key'].setErrors({ incorrect: true });
              this.notificationService.showError(
                activeTranslations.notifications_texts.error.error,
                ERROR_MESSAGES[err.code],
              );
            });
          },
        });
    }
  }

  private onCreateFormValueChange() {
    const initialValue: any = { ...this.createForm.getRawValue() };
    this.createForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
      this.hasChanged = !_.isEqual(value, initialValue);
    });
  }

  updateField() {
    const { title, is_view_card } = this.createForm.getRawValue();
    this.createForm.markAllAsTouched();

    if (title) {
      this.customAttributesService
        .updateCA({ title, is_view_card }, this.field.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.notificationService.showSuccess(
              '',
              activeTranslations.notifications_texts.success.successfully_saved,
            );
            this.dialogRef.close({
              success: true,
            });
          },
          error: () => {
            this.notificationService.showError(
              '',
              activeTranslations.notifications_texts.error.error_saving,
            );
          },
        });
    }
  }
}
